import React, { useState, useEffect, useCallback } from 'react';

import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import { searchServicenowRecords } from 'Shared/api/Servicenow.api';
import debounce from 'lodash/debounce';
import WpDataTable from 'Wpds/components/table/WpDataTable';
import Spinner from 'Shared/components/Spinner';
import WpCheckbox from 'Wpds/components/checkbox/WpCheckbox';
import { useToggle } from 'Shared/utils/hooks';
import { jsonContentStateFromString } from 'Shared/utils/draftJsHelpers';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

type Props = {
  isOpen: boolean;
  onToggle: () => void;
  onAddMeetingItem: Function;
};

const CreateItemFromServicenowModal = ({ isOpen, onToggle, onAddMeetingItem }: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearchResults, setLoadingSearchResults] = useState(false);
  const [uuidsToImport, setUuidsToImport] = useState([]);
  const [submitting, toggleSubmitting] = useToggle(false);

  const { workspace } = useWorkspace();
  const { user } = useCurrentUser();

  useEffect(() => {
    (window as any).analytics.track('Opened Modal', { modal: 'Import from ServiceNow' });
  }, []);

  const searchServicenowRecordsAndHandleResponse = s => {
    searchServicenowRecords(s).then(response => {
      setLoadingSearchResults(false);
      setSearchResults(
        response.data.map(d => ({
          sys_class_name: d.sys_class_name.value,
          sys_class_name_humanized: d.sys_class_name.display_value,
          sys_id: d.sys_id.value,
          assigned_to: d.assigned_to.display_value,
          assigned_to_email: d['assigned_to.email'].value,
          short_description: d.short_description.value,
        }))
      );
    });
  };

  const debouncedSearchServicenowRecords = useCallback(debounce(searchServicenowRecordsAndHandleResponse, 500), []);

  const handleSearchQueryChange = s => {
    setSearchQuery(s);
    if (s && s.length > 2) {
      setLoadingSearchResults(true);
      debouncedSearchServicenowRecords(s);
    }
  };

  const handleItemImportSettingChange = (sys_id, checked) => {
    const newUuidsToImport = (prev => {
      if (checked && !prev.includes(sys_id)) return [...prev, sys_id];
      if (!checked && prev.includes(sys_id)) return prev.filter(v => v !== sys_id);
      return prev;
    })(uuidsToImport);

    setUuidsToImport(newUuidsToImport);
    return true;
  };

  const ImportRecordCheckbox = ({ row }) => {
    return (
      <WpCheckbox
        id={`import_checkbox_${row.sys_id}`}
        label=""
        checked={uuidsToImport.includes(row.sys_id)}
        onChange={checked => handleItemImportSettingChange(row.sys_id, checked)}
      />
    );
  };

  const handleImportClick = () => {
    toggleSubmitting();
    uuidsToImport.forEach(uuidToImport => {
      const recordToImport = searchResults.find(searchResult => searchResult.sys_id === uuidToImport);
      if (recordToImport) {
        const args = {
          jsonBody: jsonContentStateFromString(recordToImport.short_description),
          servicenowData: {
            sys_id: recordToImport.sys_id,
            sys_class_name: recordToImport.sys_class_name,
          },
          assignmentInfo: {},
          isAssignCurrentUser: true,
        };

        const assignedUser = workspace.users.find(u => u.email === recordToImport.assigned_to_email);
        if (assignedUser) {
          args.assignmentInfo = {
            assigneePersonId: assignedUser.personId,
            assigneeAvatar: assignedUser.avatar,
            assigneeName: assignedUser.fullName,
            assignerName: user.fullName,
            assignerUserId: user.id,
          };
          args.isAssignCurrentUser = false;
        } else {
          args.isAssignCurrentUser = false;
        }

        onAddMeetingItem(args);
      }
    });

    toggleSubmitting();
    onToggle();
  };

  return (
    <WpModal size="md" isOpen={isOpen} onToggle={onToggle}>
      <WpModalHeader>Import from ServiceNow</WpModalHeader>
      <WpModalBody>
        <div className="CreateItemFromServicenowModal">
          <div className="mb-2">Choose the ServiceNow records you would like to import.</div>
          <div className="CreateItemFromServicenowModal__spacer" />
          <WpTextField
            id="servicenow-search-input"
            label="Search ServiceNow. Type in an assignee name or part of the short description."
            placeholder=""
            prefixIcon="search"
            onChange={handleSearchQueryChange}
            value={searchQuery}
          />
          <div className="mt-3">
            {loadingSearchResults && <Spinner activeIf />}
            {searchResults.length > 0 && !loadingSearchResults && (
              <WpDataTable
                columns={[
                  { name: 'Import', selector: 'import', component: ImportRecordCheckbox, noWrap: true },
                  { name: 'Type', selector: 'sys_class_name_humanized', noWrap: true },
                  { name: 'Short description', selector: 'short_description', noWrap: true },
                  { name: 'Assigned to', selector: 'assigned_to', noWrap: true },
                ]}
                rows={searchResults}
                enableSort={false}
              />
            )}
            {searchResults.length === 0 && searchQuery.length > 2 && !loadingSearchResults && (
              <div>No results found.</div>
            )}
          </div>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={onToggle} disabled={submitting}>
            Cancel
          </WpButton>
          <WpButton disabled={!uuidsToImport.length || submitting} loading={submitting} onClick={handleImportClick}>
            Import
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default CreateItemFromServicenowModal;
